import { createReducer, on } from '@ngrx/store';
import { Employee, Vehicle } from '../models/main.models';
import { Note, NoteFilters } from '../models/note.model';
import * as EmployeeActions from '../actions/employee.actions';
import * as VehicleActions from '../actions/vehicle.actions';
import * as ClientActions from '../actions/client.actions';
import * as NoteActions from '../actions/note.actions';
import * as UserProfileActions from '../actions/user-profile.actions';
import * as EmployeePaymentProfileActions from '../../payment/actions/employee-payment-profile.actions';

export interface MainState {
    activeClientId?: string;
    employees?: Employee[];
    fullProfileEmployees: Employee[];
    selectedEmployee?: Employee;
    vehicles: Vehicle[];
    notes: Note[];
    selectedNote?: Note;
    noteFilters: NoteFilters;
    activeNoteFilters: NoteFilters; // Tracks the filters used for the current notes data
    signedInUserEmailSignature?: string;
}

export const initialState: MainState = {
    // employees: [],
    fullProfileEmployees: [],
    vehicles: [],
    notes: [],
    noteFilters: {},
    activeNoteFilters: {}
};

export const mainReducer = createReducer(
    initialState,
    //Clients
    on(ClientActions.setActiveClientId, (state, { clientId }) => ({ ...state, activeClientId: clientId })),

    //Employees
    on(EmployeeActions.loadEmployeesSuccess, (state, { employees }) => ({ ...state, employees })),
    on(EmployeePaymentProfileActions.loadPaymentProfileByEmployeeIdSuccess, (state, { paymentProfile }) => {
        paymentProfile = paymentProfile || {employeeId: state.selectedEmployee?.id, payRates: {}};
        if (!state.selectedEmployee || paymentProfile.employeeId !== state.selectedEmployee.id) return state;
        return {
            ...state,
            selectedEmployee: { ...state.selectedEmployee, paymentProfile }
        }
    }),
    
    
    on(EmployeeActions.loadEmployeesFullProfileSuccess, (state, { fullProfileEmployees }) => {
        const processedEmployees = calculateHasRatesEnabled(fullProfileEmployees);
        return { ...state, fullProfileEmployees: processedEmployees || [] }
    }),
    on(EmployeeActions.loadEmployeeByIdSuccess, (state, { selectedEmployee }) => ({ ...state, selectedEmployee })),
    on(EmployeeActions.cleanSelectedEmployee, (state, { }) => ({ ...state, selectedEmployee: undefined })),
    on(EmployeeActions.loadEmployeeFullProfileByIDSuccess, (state, { selectedEmployee }) => ({ ...state, selectedEmployee })),
    on(EmployeeActions.createEmployeeSuccess, (state, { employee }) => ({
        ...state,
        employees: state.employees ? [...state.employees, employee] : [employee],
        selectedEmployee: employee
    })),
    on(EmployeeActions.updateEmployeeSuccess, (state, { employee }) => {
        const employees = state.employees?.map(emp => emp.id === employee.id ? employee : emp);
        const fullProfileEmployees = state.fullProfileEmployees?.map(emp => emp.id === employee.id ? { ...employee, paymentProfile: emp.paymentProfile } : emp);
        const selectedEmployee = state.selectedEmployee
        return {
            ...state,
            selectedEmployee: { ...employee, paymentProfile: selectedEmployee?.paymentProfile },
            employees: employees,
            fullProfileEmployees: fullProfileEmployees
        }
    }
    ),
    on(EmployeePaymentProfileActions.upsertEmployeePaymentProfileSuccess, (state, { paymentProfile }) => {
        if (!state.selectedEmployee) return state;

        const updatedEmployee = { ...state.selectedEmployee, paymentProfile };
        const [employeeWithRates] = calculateHasRatesEnabled([updatedEmployee]);

        // Update the employee in fullProfileEmployees if it exists
        const updatedFullProfileEmployees = state.fullProfileEmployees.map(emp =>
            emp.id === state.selectedEmployee?.id ? employeeWithRates : emp
        );
        return {
            ...state,
            selectedEmployee: employeeWithRates,
            fullProfileEmployees: updatedFullProfileEmployees
        };
    }),
    on(EmployeeActions.deleteEmployeeSuccess, (state, { employeeId }) => ({
        ...state,
        employees: state.employees?.filter(e => e.id !== employeeId),
        fullProfileEmployees: state.fullProfileEmployees?.filter(e => e.id !== employeeId),
        selectedEmployee: state.selectedEmployee?.id === employeeId ? undefined : state.selectedEmployee
    })),

    //Vehicles
    on(VehicleActions.loadVehiclesSuccess, (state, { vehicles }) => ({ ...state, vehicles: vehicles || [] })),

    // Notes
    on(NoteActions.getAllNotesSuccess, (state, { notes, filters }) => ({
        ...state,
        notes: notes || [],
        activeNoteFilters: filters // Store the filters that were used to load these notes
    })),
    on(NoteActions.createNoteSuccess, (state, { note }) => ({ ...state, notes: [note, ...state.notes] })),
    on(NoteActions.updateNoteSuccess, (state, { note }) => ({
        ...state,
        notes: state.notes.map(n => n.id === note.id ? note : n),
        selectedNote: state.selectedNote?.id === note.id ? note : state.selectedNote
    })),
    on(NoteActions.closeNoteSuccess, (state, { note }) => ({
        ...state,
        notes: state.notes.map(n => n.id === note.id ? note : n),
        selectedNote: state.selectedNote?.id === note.id ? note : state.selectedNote
    })),

    // Note Filters (for page-specific filtering)
    on(NoteActions.setNoteFilters, (state, { filters }) => ({
        ...state,
        noteFilters: filters
    })),
    on(NoteActions.clearNoteFilters, (state) => ({
        ...state,
        noteFilters: {}
    })),

    // User Profile - Email Signature
    on(UserProfileActions.loadEmailSignatureFromLoggedInUserSuccess, (state, { emailSignature }) => ({
        ...state,
        signedInUserEmailSignature: emailSignature
    }))
);

function calculateHasRatesEnabled(employees: Employee[]) {
    return employees?.map(employee => {
        const hasRatesEnabled: { [key: string]: boolean } = {};
        if (employee.paymentProfile?.payRates) {
            // Iterate through each payrate type (e.g., standardPayroll)
            Object.keys(employee.paymentProfile.payRates).forEach(payrateType => {
                const payrateData = employee.paymentProfile?.payRates?.[payrateType];

                // Check if there's at least one rate configured
                let hasAnyRate = false;

                // Check different rate structures (byMiles, byHours, etc.)
                if (payrateData && Object.keys(payrateData).length > 0) {
                    hasAnyRate = true;
                }
                hasRatesEnabled[payrateType] = hasAnyRate;
            });
        }
        return {
            ...employee,
            hasRatesEnabled
        };
    });
}
